/**
 * Admin Bundle Detail Page
 *
 * Shows comprehensive detail for a single bundle including:
 * - Header with status and dates
 * - Seller/Buyer info with addresses and usernames
 * - Collection info
 * - NFT grid showing all items in bundle
 * - Fee breakdown card
 * - Transaction hashes linked to Bithomp
 * - Status timeline
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';

// ==================== API ====================

const API_BASE = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

async function fetchWithAuth(endpoint: string) {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// ==================== Types ====================

interface BundleDetail {
  id: string;
  seller_address: string;
  collection_id: string;
  price_drops: string;
  status: string;
  buyer_address: string | null;
  payment_tx_hash: string | null;
  expires_at: string | null;
  sold_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  seller_username: string | null;
  buyer_username: string | null;
  collection_name: string | null;
  collection_slug: string | null;
  collection_image_url: string | null;
}

interface BundleItem {
  id: string;
  bundle_id: string;
  nft_token_id: string;
  sell_offer_index: string | null;
  status: string;
  offer_tx_hash: string | null;
  created_at: string;
  nft_name: string | null;
  nft_image_url: string | null;
  nft_thumbnail_url: string | null;
  rarity_rank: number | null;
}

interface FeeBreakdown {
  priceDrops: string;
  sellerFeeDrops: string;
  buyerFeeDrops: string;
  sellerReceivesDrops: string;
  buyerPaysDrops: string;
  totalPlatformFeeDrops: string;
}

interface BundleDetailResponse {
  bundle: BundleDetail;
  items: BundleItem[];
  feeBreakdown: FeeBreakdown;
}

// ==================== Helpers ====================

function formatXrp(drops: string | number | null | undefined): string {
  if (drops === null || drops === undefined || drops === '0') return '-';
  return `${(Number(drops) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 6 })} XRP`;
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function truncateAddress(address: string): string {
  return `${address.slice(0, 8)}...${address.slice(-4)}`;
}

function getBundleStatusColor(status: string): string {
  switch (status) {
    case 'live':
      return 'bg-green-100 text-green-700';
    case 'sold':
      return 'bg-blue-100 text-blue-700';
    case 'cancelled':
      return 'bg-red-100 text-red-700';
    case 'expired':
      return 'bg-gray-100 text-gray-600';
    case 'pending':
      return 'bg-yellow-100 text-yellow-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

function getItemStatusColor(status: string): string {
  switch (status) {
    case 'transferred':
      return 'bg-green-100 text-green-700';
    case 'confirmed':
      return 'bg-blue-100 text-blue-700';
    case 'pending':
      return 'bg-yellow-100 text-yellow-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

// ==================== Section Components ====================

function SectionHeader({ title }: { title: string }) {
  return (
    <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
      {title}
    </h2>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex justify-between py-1.5">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm text-gray-900 ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

// ==================== Timeline Component ====================

interface TimelineStep {
  label: string;
  date: string | null | undefined;
  active: boolean;
  completed: boolean;
}

function StatusTimeline({ steps }: { steps: TimelineStep[] }) {
  return (
    <div className="flex items-center gap-0">
      {steps.map((step, idx) => (
        <React.Fragment key={step.label}>
          <div className="flex flex-col items-center">
            <div
              className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium ${
                step.completed
                  ? 'bg-blue-600 text-white'
                  : step.active
                    ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-600'
                    : 'bg-gray-100 text-gray-400'
              }`}
            >
              {step.completed ? (
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                idx + 1
              )}
            </div>
            <div className="mt-1 text-xs text-gray-500 text-center whitespace-nowrap">{step.label}</div>
            {step.date && (
              <div className="text-xs text-gray-400 text-center">{formatDate(step.date)}</div>
            )}
          </div>
          {idx < steps.length - 1 && (
            <div
              className={`h-0.5 flex-1 min-w-[40px] mt-[-20px] ${
                step.completed ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ==================== Main Component ====================

export function AdminBundleDetailPage() {
  const { bundleId } = useParams<{ bundleId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<BundleDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!bundleId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await fetchWithAuth(`/admin/bundles/${encodeURIComponent(bundleId)}`);
      if (result.success) {
        setData(result.data);
      } else {
        throw new Error(result.error || 'Failed to load bundle detail');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load bundle detail');
    } finally {
      setLoading(false);
    }
  }, [bundleId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-600">Loading bundle detail...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="mx-auto max-w-5xl">
          <button
            onClick={() => navigate('/admin/bundles')}
            className="mb-4 text-sm text-blue-600 hover:text-blue-800"
          >
            &larr; Back to bundles
          </button>
          <ErrorDisplay error={error} onRetry={loadData} />
        </div>
      </AdminLayout>
    );
  }

  if (!data) return null;

  const { bundle, items, feeBreakdown } = data;

  // Build timeline steps
  const timelineSteps: TimelineStep[] = [
    {
      label: 'Created',
      date: bundle.created_at,
      active: bundle.status === 'pending',
      completed: true,
    },
    {
      label: 'Live',
      date: bundle.status !== 'pending' ? bundle.updated_at : null,
      active: bundle.status === 'live',
      completed: ['live', 'sold', 'cancelled', 'expired'].includes(bundle.status),
    },
  ];

  if (bundle.status === 'sold') {
    timelineSteps.push({
      label: 'Sold',
      date: bundle.sold_at,
      active: false,
      completed: true,
    });
  } else if (bundle.status === 'cancelled') {
    timelineSteps.push({
      label: 'Cancelled',
      date: bundle.cancelled_at,
      active: false,
      completed: true,
    });
  } else if (bundle.status === 'expired') {
    timelineSteps.push({
      label: 'Expired',
      date: bundle.expires_at,
      active: false,
      completed: true,
    });
  } else {
    timelineSteps.push({
      label: 'Sold',
      date: null,
      active: false,
      completed: false,
    });
  }

  return (
    <AdminLayout>
      <div className="mx-auto max-w-5xl">
        {/* Back link */}
        <button
          onClick={() => navigate('/admin/bundles')}
          className="mb-4 text-sm text-blue-600 hover:text-blue-800"
        >
          &larr; Back to bundles
        </button>

        {/* ==================== 1. Header ==================== */}
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">Bundle</h1>
                <span className={`inline-flex rounded-full px-3 py-1 text-sm font-medium ${getBundleStatusColor(bundle.status)}`}>
                  {bundle.status}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                <span className="font-mono">{bundle.id}</span>
                <button
                  onClick={() => handleCopy(bundle.id, 'bundle-id')}
                  className="text-gray-400 hover:text-gray-600"
                  title="Copy bundle ID"
                >
                  {copiedField === 'bundle-id' ? (
                    <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                  )}
                </button>
              </div>
            </div>
            <button
              onClick={loadData}
              className="rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Refresh
            </button>
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <InfoRow label="Created" value={formatDate(bundle.created_at)} />
            <InfoRow label="Expires" value={formatDate(bundle.expires_at)} />
            <InfoRow
              label={bundle.status === 'sold' ? 'Sold' : bundle.status === 'cancelled' ? 'Cancelled' : 'Updated'}
              value={formatDate(bundle.sold_at || bundle.cancelled_at || bundle.updated_at)}
            />
          </div>
        </div>

        {/* ==================== 2. Status Timeline ==================== */}
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <SectionHeader title="Status Timeline" />
          <StatusTimeline steps={timelineSteps} />
        </div>

        {/* ==================== 3. Seller / Buyer ==================== */}
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <SectionHeader title="Parties" />
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <div className="text-xs font-medium uppercase text-gray-500 mb-2">Seller</div>
              <div className="text-sm font-medium text-gray-900">
                {bundle.seller_username || 'No username'}
              </div>
              <div className="text-xs font-mono text-gray-500 mt-1">
                {bundle.seller_address}
              </div>
              <button
                onClick={() => handleCopy(bundle.seller_address, 'seller')}
                className="mt-1 text-xs text-blue-600 hover:text-blue-800"
              >
                {copiedField === 'seller' ? 'Copied!' : 'Copy address'}
              </button>
            </div>
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <div className="text-xs font-medium uppercase text-gray-500 mb-2">Buyer</div>
              {bundle.buyer_address ? (
                <>
                  <div className="text-sm font-medium text-gray-900">
                    {bundle.buyer_username || 'No username'}
                  </div>
                  <div className="text-xs font-mono text-gray-500 mt-1">
                    {bundle.buyer_address}
                  </div>
                  <button
                    onClick={() => handleCopy(bundle.buyer_address!, 'buyer')}
                    className="mt-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    {copiedField === 'buyer' ? 'Copied!' : 'Copy address'}
                  </button>
                </>
              ) : (
                <div className="text-sm text-gray-400 italic">No buyer yet</div>
              )}
            </div>
          </div>
        </div>

        {/* ==================== 4. Collection ==================== */}
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <SectionHeader title="Collection" />
          <div className="flex items-center gap-3">
            {bundle.collection_image_url && (
              <img
                src={bundle.collection_image_url}
                alt=""
                className="h-10 w-10 rounded-full object-cover"
              />
            )}
            <div>
              <div className="text-sm font-medium text-gray-900">
                {bundle.collection_name || 'Unknown Collection'}
              </div>
              {bundle.collection_slug && (
                <div className="text-xs text-gray-500">/{bundle.collection_slug}</div>
              )}
              <div className="text-xs font-mono text-gray-400 mt-0.5">{bundle.collection_id}</div>
            </div>
          </div>
        </div>

        {/* ==================== 5. Fee Breakdown ==================== */}
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <SectionHeader title="Fee Breakdown" />
          <div className="space-y-1">
            <InfoRow label="Bundle Price" value={formatXrp(feeBreakdown.priceDrops)} />
            <div className="border-t border-gray-100 my-2" />
            <InfoRow label="Seller Fee (1%)" value={formatXrp(feeBreakdown.sellerFeeDrops)} />
            <InfoRow label="Seller Receives" value={
              <span className="font-medium text-green-700">{formatXrp(feeBreakdown.sellerReceivesDrops)}</span>
            } />
            <div className="border-t border-gray-100 my-2" />
            <InfoRow label="Buyer Fee (1%)" value={formatXrp(feeBreakdown.buyerFeeDrops)} />
            <InfoRow label="Buyer Pays" value={
              <span className="font-medium text-blue-700">{formatXrp(feeBreakdown.buyerPaysDrops)}</span>
            } />
            <div className="border-t border-gray-100 my-2" />
            <InfoRow label="Platform Total (2%)" value={
              <span className="font-medium text-gray-900">{formatXrp(feeBreakdown.totalPlatformFeeDrops)}</span>
            } />
          </div>
        </div>

        {/* ==================== 6. Transaction Hashes ==================== */}
        {bundle.payment_tx_hash && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <SectionHeader title="Transactions" />
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Payment TX</span>
                <div className="flex items-center gap-2">
                  <a
                    href={`https://bithomp.com/explorer/${encodeURIComponent(bundle.payment_tx_hash)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 font-mono"
                  >
                    {truncateAddress(bundle.payment_tx_hash)}
                  </a>
                  <button
                    onClick={() => handleCopy(bundle.payment_tx_hash!, 'payment-tx')}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {copiedField === 'payment-tx' ? (
                      <svg className="h-3.5 w-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    ) : (
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== 7. Bundle Items (NFT Grid) ==================== */}
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <SectionHeader title={`Bundle Items (${items.length})`} />
          {items.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No items in this bundle</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((item) => (
                <div key={item.id} className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="flex items-start gap-3">
                    {item.nft_thumbnail_url || item.nft_image_url ? (
                      <img
                        src={item.nft_thumbnail_url || item.nft_image_url || ''}
                        alt={item.nft_name || 'NFT'}
                        className="h-16 w-16 rounded-md object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="h-16 w-16 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs text-gray-400">No img</span>
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {item.nft_name || 'Unnamed NFT'}
                      </div>
                      {item.rarity_rank && (
                        <span className="inline-flex mt-1 rounded bg-purple-50 px-1.5 py-0.5 text-xs text-purple-700">
                          Rank #{item.rarity_rank}
                        </span>
                      )}
                      <div className="mt-1 text-xs font-mono text-gray-400 truncate">
                        {item.nft_token_id.slice(0, 16)}...
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className={`inline-flex rounded-full px-1.5 py-0.5 text-xs font-medium ${getItemStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                        {item.offer_tx_hash && (
                          <a
                            href={`https://bithomp.com/explorer/${encodeURIComponent(item.offer_tx_hash)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 font-mono"
                            title={item.offer_tx_hash}
                          >
                            tx: {item.offer_tx_hash.slice(0, 8)}...
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
